import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Users, Truck, DollarSign, ChevronDown, ChevronUp, Plus, Trash2, MinusCircle, PlusCircle, Download, Mail, Loader2, RotateCw, CheckCircle2, AlertCircle, Clock, Lock, LockOpen } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from '../components/ui/sonner';
import { PeriodDeductionsPanel, PendingRevisionBanner, usePayrollPeriodDeductions } from '../components/PeriodDeductionsPanel';
import { openReceipt } from '../components/FuelReceiptUploader';
import AddLoadToPayrollModal from '../components/AddLoadToPayrollModal';
import { PayrollStatusBadge, SendPayrollButton, PendingRevisionNotice } from '../components/PayrollSendControls';
import { AdminFeeSummaryCard, EntryAdminFeeRow } from '../components/AdminFeeControls';
import { RevisionDiffPanel } from '../components/RevisionDiff';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAY_TYPE_LABELS = {
  salary: 'Salary',
  per_mile: 'Per Mile',
  hourly: 'Hourly',
  percentage: 'Percentage',
};

const DEDUCTION_LABELS = {
  // New permanent labels
  eld: 'ELD',
  parking: 'Parking',
  insurance: 'Insurance',
  lease_on: 'Lease-on',
  trailer_rental: 'Trailer Rental',
  dispatch_fee: 'Dispatch Fee',
  factoring_fee: 'Factoring Fee',
  // Legacy labels (still rendered for old data)
  truck_lease: 'Truck Lease Fee', fuel: 'Fuel Expense', other: 'Other Deduction',
  monthly_parking: 'Monthly Parking',
  daily_parking: 'Daily Parking',
  eld_fee: 'ELD Fee',
  toll: 'Toll',
  truck_wash: 'Truck Wash',
  repair: 'Repair',
  lease_on_fee: 'Lease-on Fee',
  trailer_fee: 'Trailer Fee',
};

// Variable per-period expense categories — picked when creating a Deduction adjustment
// Order matches the backend VALID_EXPENSE_CATEGORIES enum for consistency.
const EXPENSE_CATEGORIES = [
  { value: 'fuel', label: 'Fuel' },
  { value: 'toll', label: 'Toll' },
  { value: 'truck_wash', label: 'Truck Wash' },
  { value: 'repair', label: 'Repair' },
  { value: 'ifta_tax', label: 'IFTA Tax' },
  { value: 'ifta_filing', label: 'IFTA Filing' },
  { value: 'plate_renewal', label: 'Plate Renewal' },
  { value: 'permit', label: 'Permit' },
  { value: 'drug_test', label: 'Drug Test' },
  { value: 'trip_repair', label: 'Trip Repair' },
  { value: 'tax_2290', label: '2290 Tax' },
  { value: 'advance_repayment', label: 'Advance Repayment' },
  { value: 'other', label: 'Other' },
];
const EXPENSE_CATEGORY_LABEL = Object.fromEntries(EXPENSE_CATEGORIES.map(e => [e.value, e.label]));


// Delivery status pill for the per-driver paystub email.
// Pulls from `paystub_email_status` (set on send + updated by the Resend webhook).
const EMAIL_STATUS_META = {
  sent:        { label: 'Sent',        color: 'bg-blue-50 text-blue-700 border-blue-200',           Icon: Mail,         testidStatus: 'sent' },
  delivered:   { label: 'Delivered',   color: 'bg-emerald-50 text-emerald-700 border-emerald-200',  Icon: CheckCircle2, testidStatus: 'delivered' },
  opened:      { label: 'Opened',      color: 'bg-emerald-50 text-emerald-700 border-emerald-200',  Icon: CheckCircle2, testidStatus: 'opened' },
  clicked:     { label: 'Clicked',     color: 'bg-emerald-50 text-emerald-700 border-emerald-200',  Icon: CheckCircle2, testidStatus: 'clicked' },
  delayed:     { label: 'Delayed',     color: 'bg-amber-50 text-amber-700 border-amber-200',        Icon: Clock,        testidStatus: 'delayed' },
  bounced:     { label: 'Bounced',     color: 'bg-red-50 text-red-700 border-red-200',              Icon: AlertCircle,  testidStatus: 'bounced' },
  complained:  { label: 'Complained',  color: 'bg-red-50 text-red-700 border-red-200',              Icon: AlertCircle,  testidStatus: 'complained' },
  failed:      { label: 'Send failed', color: 'bg-red-50 text-red-700 border-red-200',              Icon: AlertCircle,  testidStatus: 'failed' },
};

const EmailStatusPill = ({ entry }) => {
  const status = entry?.paystub_email_status;
  if (!status) return null;
  const meta = EMAIL_STATUS_META[status] || { label: status, color: 'bg-slate-50 text-slate-600 border-slate-200', Icon: Mail, testidStatus: 'unknown' };
  const { Icon } = meta;
  const at = entry?.paystub_email_status_at;
  const tooltip = `Paystub ${meta.label.toLowerCase()}${entry?.paystub_email_to ? ' • ' + entry.paystub_email_to : ''}${at ? ' • ' + new Date(at).toLocaleString() : ''}`;
  return (
    <span
      data-testid={`email-status-${entry.id}-${meta.testidStatus}`}
      title={tooltip}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${meta.color}`}
      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};


// Heuristic keyword → category map used to auto-pick the dropdown as the admin types.
// Matching is case-insensitive and uses word-boundary semantics where useful.
const CATEGORY_KEYWORDS = [
  { cat: 'fuel',              re: /\b(fuel|diesel|gas|gallons?|pilot|loves|ta\s+travel|petro|flying\s*j|exxon|shell|chevron|valero|bp|sunoco|sapp|kwik|wawa)\b/i },
  { cat: 'toll',              re: /\b(toll|tolls|ez[- ]?pass|sunpass|fastrak|i[- ]?pass|e[- ]?zpass|turnpike|plate\s*scan)\b/i },
  { cat: 'truck_wash',        re: /\b(wash|blue\s*beacon|truck\s*wash|trailer\s*wash)\b/i },
  { cat: 'trip_repair',       re: /\b(breakdown|road[- ]?call|trip\s*repair|emergency\s*repair)\b/i },
  { cat: 'repair',            re: /\b(repair|mechanic|brake|tire|alternator|battery|clutch|transmission|fix\s+truck)\b/i },
  { cat: 'ifta_filing',       re: /\bifta\b.*\b(filing|file|return)\b/i },
  { cat: 'ifta_tax',          re: /\bifta\b/i },
  { cat: 'plate_renewal',     re: /\b(plate|registration|tag\s*renewal|renewal)\b/i },
  { cat: 'permit',            re: /\b(permit|oversize|overweight|trip\s*permit|fuel\s*permit)\b/i },
  { cat: 'drug_test',         re: /\b(drug\s*(test|screen)|dot\s*test|sap)\b/i },
  { cat: 'tax_2290',          re: /\b(2290|hvut|heavy\s*vehicle\s*use\s*tax)\b/i },
  { cat: 'advance_repayment', re: /\b(advance|cash\s*advance|loan|payback|owed)\b/i },
];

const guessExpenseCategory = (text) => {
  if (!text) return null;
  for (const { cat, re } of CATEGORY_KEYWORDS) {
    if (re.test(text)) return cat;
  }
  return null;
};

const PayrollDetail = () => {
  const { user: authUser } = useAuth();
  const isAdmin = ['company_admin', 'super_admin'].includes(authUser?.role);
  const { payrollId } = useParams();
  const navigate = useNavigate();
  const [payroll, setPayroll] = useState(null);
  const [entries, setEntries] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [addLoadEntry, setAddLoadEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [showAdjForm, setShowAdjForm] = useState(false);
  const [adjForm, setAdjForm] = useState({ adjustment_type: 'compensation', amount: '', reason: '', expense_category: 'fuel', category_manual: false, applies_to: 'all', selected_drivers: [] });
  const [adjError, setAdjError] = useState('');
  const [savingAdj, setSavingAdj] = useState(false);

  useEffect(() => {
    fetchDetail();
  }, [payrollId]);
  const periodDeductions = usePayrollPeriodDeductions(payrollId);

  // A payroll run that was regenerated/revised gets a NEW id — a tab still on the old id must be redirected, not left with "Payroll not found".
  const handleStaleRun = (err) => {
    if (err?.response?.status !== 404) return false;
    toast.error('This payroll run was regenerated or deleted — opening the current payroll list.');
    navigate('/payroll', { replace: true });
    return true;
  };

  const fetchDetail = async () => {
    try {
      const res = await axios.get(`${API}/payrolls/${payrollId}`, { withCredentials: true });
      setPayroll(res.data.payroll);
      setEntries(res.data.entries);
      setAdjustments(res.data.adjustments || []);
      setError('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to load payroll');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAdj = async (e) => {
    e.preventDefault();
    setAdjError('');
    const amt = parseFloat(adjForm.amount);
    if (isNaN(amt) || amt <= 0) {
      setAdjError('Amount must be greater than zero');
      return;
    }
    if (!adjForm.reason.trim()) {
      setAdjError('Reason is required');
      return;
    }
    setSavingAdj(true);
    try {
      const body = {
        adjustment_type: adjForm.adjustment_type,
        amount: amt,
        reason: adjForm.reason.trim(),
        expense_category: adjForm.adjustment_type === 'deduction' ? adjForm.expense_category : null,
        applies_to_driver_ids: adjForm.applies_to === 'all' ? null : adjForm.selected_drivers,
      };
      await axios.post(`${API}/payrolls/${payrollId}/adjustments`, body, { withCredentials: true });
      setShowAdjForm(false);
      setAdjForm({ adjustment_type: 'compensation', amount: '', reason: '', expense_category: 'fuel', category_manual: false, applies_to: 'all', selected_drivers: [] });
      fetchDetail();
    } catch (err) {
      if (handleStaleRun(err)) return;
      const detail = err.response?.data?.detail;
      setAdjError(typeof detail === 'string' ? detail : 'Failed to save adjustment');
    } finally {
      setSavingAdj(false);
    }
  };

  const handleDeleteAdj = async (adjId) => {
    if (!window.confirm('Delete this adjustment?')) return;
    try {
      await axios.delete(`${API}/payrolls/${payrollId}/adjustments/${adjId}`, { withCredentials: true });
      fetchDetail();
    } catch (err) {
      alert('Failed to delete adjustment');
    }
  };

  const toggleDriverSelect = (driverId) => {
    setAdjForm((prev) => {
      const arr = prev.selected_drivers.includes(driverId)
        ? prev.selected_drivers.filter((x) => x !== driverId)
        : [...prev.selected_drivers, driverId];
      return { ...prev, selected_drivers: arr };
    });
  };

  const handleDownloadPaystub = async (entry) => {
    try {
      const res = await axios.get(
        `${API}/payrolls/${payrollId}/entries/${entry.id}/paystub.pdf`,
        { withCredentials: true, responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (entry.driver_name || 'driver').replace(/\s+/g, '_');
      a.download = `paystub_${safeName}_${payroll.week_end}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to generate paystub PDF');
    }
  };

  const [resendingEntry, setResendingEntry] = useState(null);
  const handleResendPaystub = async (entry) => {
    setResendingEntry(entry.id);
    try {
      const res = await axios.post(
        `${API}/payrolls/${payrollId}/entries/${entry.id}/email-paystub`,
        {},
        { withCredentials: true },
      );
      toast.success(`Paystub emailed to ${res.data.to}`);
      fetchDetail();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to email paystub');
    } finally {
      setResendingEntry(null);
    }
  };

  const [recalculating, setRecalculating] = useState(false);
  const handleRecalculate = async (reasonText) => {
    const sentAlready = payroll?.status !== 'draft';
    const reason = typeof reasonText === 'string' ? reasonText : window.prompt(
      'Apply a payroll revision?\n\nThis rebuilds the run from current loads and deductions, reverses and re-applies advance/escrow effects, ' +
      'and records the revision in the audit log. ' + (sentAlready
        ? 'This payroll was already SENT: the result becomes a PENDING REVISION — drivers keep seeing the sent version until you explicitly send the revision. No emails are sent now.'
        : 'The result is a new DRAFT — no emails are sent until you click Send payroll.') + '\n\nReason for this revision (recorded):', ''
    );
    if (reason === null) return;
    setRecalculating(true);
    try {
      const res = await axios.post(`${API}/payrolls/${payrollId}/recalculate`, { reason }, { withCredentials: true });
      toast.success(res.data?.revision_of
        ? `Revision ${res.data?.revision || ''} saved as a pending draft. Drivers still see the sent version — review, then click Send payroll to replace it.`
        : `Payroll revision ${res.data?.revision || ''} applied as a draft. Review it, then click Send payroll to release it to drivers.`);
      if (res.data?.id && res.data.id !== payrollId) {
        navigate(`/payroll/${res.data.id}`, { replace: true });
      } else {
        fetchDetail();
        periodDeductions.refresh();
      }
    } catch (err) {
      if (handleStaleRun(err)) return;
      toast.error(err?.response?.data?.detail || 'Failed to apply payroll revision');
    } finally {
      setRecalculating(false);
    }
  };

  const [togglingLock, setTogglingLock] = useState(false);
  const handleToggleLock = async () => {
    const locking = !payroll.locked;
    if (!window.confirm(
      locking
        ? 'Lock this pay week? Its loads and paystubs will be frozen — no edits, re-dating, status changes, deletes or recalculation until you unlock it.'
        : 'Unlock this pay week? Its loads and paystubs become editable again.'
    )) return;
    setTogglingLock(true);
    try {
      await axios.post(`${API}/payrolls/${payrollId}/${locking ? 'lock' : 'unlock'}`, {}, { withCredentials: true });
      toast.success(locking ? 'Pay week locked.' : 'Pay week unlocked.');
      fetchDetail();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update lock');
    } finally {
      setTogglingLock(false);
    }
  };

  if (loading) {
    return <DashboardLayout><div className="p-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading...</div></DashboardLayout>;
  }
  if (error || !payroll) {
    return <DashboardLayout><div className="p-8 text-red-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{error || 'Payroll not found'}</div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="p-8 max-w-5xl mx-auto">
        <button
          data-testid="back-to-payroll-button"
          onClick={() => navigate('/payroll')}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
          Back to Payroll
        </button>

        <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-8 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Payroll Run</h1>
              <p data-testid="payroll-week" className="text-sm text-slate-600 flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <Calendar className="w-4 h-4" />
                {payroll.week_start} → {payroll.week_end}
              </p>
              <p className="text-xs text-slate-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Generated {new Date(payroll.generated_at).toLocaleString()}</p>
              {(payroll.revision || 1) > 1 && (
                <span data-testid="payroll-revision-badge" className="mt-2 mr-2 inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-800 rounded-md px-2.5 py-1 text-xs font-semibold"
                  title={payroll.revision_reason ? `Reason: ${payroll.revision_reason}` : 'Revised payroll run'}>
                  Revision {payroll.revision}{payroll.revision_reason ? ` — ${payroll.revision_reason}` : ''}
                </span>
              )}
              {payroll.is_supplemental && (
                <div data-testid="payroll-supplemental-badge" className="mt-2 mr-2 inline-flex items-center gap-1 bg-emerald-50 border border-emerald-300 text-emerald-900 rounded-md px-2.5 py-1 text-xs font-semibold" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  Supplemental payroll · authorized by {payroll.authorized_by_name} · {payroll.supplemental_reason}
                  <button type="button" className="underline ml-1" onClick={() => navigate(`/payroll/${payroll.parent_payroll_id}`)}>view original</button>
                </div>
              )}
              <div className="mt-2 mr-2 inline-flex"><PayrollStatusBadge payroll={payroll} /></div>
              {payroll.locked && (
                <div data-testid="payroll-locked-audit" className="mt-2 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">
                  <Lock className="w-3.5 h-3.5 text-amber-600" />
                  <span className="text-xs font-semibold text-amber-800" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    Locked{payroll.locked_by_name ? ` by ${payroll.locked_by_name}` : ''}
                    {payroll.locked_at ? ` on ${new Date(payroll.locked_at).toLocaleString()}` : ''}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <SendPayrollButton payroll={payroll} isAdmin={isAdmin} onSent={fetchDetail} entryCount={entries.length} />
              <button
                data-testid="recalculate-payroll"
                onClick={handleRecalculate}
                disabled={recalculating || payroll.locked || payroll.status === 'superseded'}
                title={payroll.locked ? 'Unlock this pay week to recalculate' : (payroll.status === 'superseded' ? 'Replaced by a later revision' : 'Re-scan completed loads for this period and rebuild every entry')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 border border-slate-300 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                {recalculating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
                {recalculating ? 'Recalculating…' : 'Recalculate'}
              </button>
              <button
                data-testid="toggle-lock-payroll"
                onClick={handleToggleLock}
                disabled={togglingLock}
                title={payroll.locked ? 'Unlock this pay week' : 'Lock this pay week to freeze its loads and paystubs'}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border rounded-md transition-colors disabled:opacity-50 ${payroll.locked ? 'text-amber-800 bg-amber-50 border-amber-300 hover:bg-amber-100' : 'text-slate-700 border-slate-300 hover:bg-slate-100'}`}
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                {togglingLock ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (payroll.locked ? <LockOpen className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />)}
                {payroll.locked ? 'Unlock' : 'Lock'}
              </button>
              <span className={`px-3 py-1 rounded text-xs font-semibold ${payroll.status === 'draft' ? 'bg-slate-100 text-slate-700' : 'bg-green-100 text-green-700'}`}>{payroll.status}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            <div data-testid="summary-gross" className="border border-slate-200 rounded-md p-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Gross Pay</p>
              <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>${Number(payroll.total_gross || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>
            <div data-testid="summary-compensation" className="border border-slate-200 rounded-md p-4 bg-blue-50 border-blue-200">
              <p className="text-xs font-semibold uppercase tracking-widest text-blue-700 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>+ Compensation</p>
              <p className="text-xl font-bold text-blue-800" style={{ fontFamily: 'Work Sans, sans-serif' }}>${(payroll.total_compensation || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>
            <div data-testid="summary-standing-deductions" className="border border-slate-200 rounded-md p-4 bg-orange-50 border-orange-200">
              <p className="text-xs font-semibold uppercase tracking-widest text-orange-700 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>− Standing Ded.</p>
              <p className="text-xl font-bold text-orange-800" style={{ fontFamily: 'Work Sans, sans-serif' }}>${(payroll.total_standing_deductions || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>
            <div data-testid="summary-custom-deductions" className="border border-slate-200 rounded-md p-4 bg-red-50 border-red-200">
              <p className="text-xs font-semibold uppercase tracking-widest text-red-700 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>− Custom Ded.</p>
              <p className="text-xl font-bold text-red-800" style={{ fontFamily: 'Work Sans, sans-serif' }}>${(payroll.total_custom_deductions || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>
            <div data-testid="summary-net" className={`border rounded-md p-4 ${payroll.total_net < 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
              <p className={`text-xs font-semibold uppercase tracking-widest mb-1 ${payroll.total_net < 0 ? 'text-red-700' : 'text-green-700'}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>= Net Pay</p>
              <p className={`text-xl font-bold ${payroll.total_net < 0 ? 'text-red-700' : 'text-green-800'}`} style={{ fontFamily: 'Work Sans, sans-serif' }}>${payroll.total_net.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>
          </div>

          <p className="text-xs text-slate-500 italic mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Formula: Gross + Compensation − Standing Deductions − Custom Deductions = Net</p>
          <AdminFeeSummaryCard payroll={payroll} entries={entries} isAdmin={isAdmin} onApplied={(d) => { if (d?.id && d.id !== payrollId) navigate(`/payroll/${d.id}`, { replace: true }); else fetchDetail(); }} />
        </div>

        <PendingRevisionNotice payroll={payroll} onDiscarded={() => { if (!payroll?.revision_of) fetchDetail(); }} />
        {payroll.revision_of && payroll.status === 'draft' && <RevisionDiffPanel payrollId={payrollId} />}

        <PendingRevisionBanner pending={periodDeductions.pending} revision={payroll.revision} locked={payroll.locked}
          applying={recalculating} onApply={() => handleRecalculate()} />

        {/* ADJUSTMENTS */}
        <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Custom Adjustments ({adjustments.length})</h2>
            {!showAdjForm && (
              <button
                data-testid="add-adjustment-button"
                onClick={() => { setShowAdjForm(true); setAdjError(''); }}
                className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <Plus className="w-4 h-4" />
                Add Adjustment
              </button>
            )}
          </div>

          {adjError && (
            <div data-testid="adjustment-error" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">{adjError}</div>
          )}

          {showAdjForm && (
            <form onSubmit={handleSubmitAdj} className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-md">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    <label data-testid="adj-type-compensation" className={`flex items-center gap-2 p-3 border rounded-md cursor-pointer ${adjForm.adjustment_type === 'compensation' ? 'border-[#002FA7] bg-blue-50' : 'border-slate-300 bg-white'}`}>
                      <input type="radio" name="adj_type" value="compensation" checked={adjForm.adjustment_type === 'compensation'} onChange={(e) => setAdjForm({ ...adjForm, adjustment_type: e.target.value })} />
                      <PlusCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Compensation</span>
                    </label>
                    <label data-testid="adj-type-deduction" className={`flex items-center gap-2 p-3 border rounded-md cursor-pointer ${adjForm.adjustment_type === 'deduction' ? 'border-[#002FA7] bg-blue-50' : 'border-slate-300 bg-white'}`}>
                      <input type="radio" name="adj_type" value="deduction" checked={adjForm.adjustment_type === 'deduction'} onChange={(e) => setAdjForm({ ...adjForm, adjustment_type: e.target.value })} />
                      <MinusCircle className="w-4 h-4 text-red-600" />
                      <span className="text-sm font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Deduction</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Amount ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    data-testid="adj-amount-input"
                    value={adjForm.amount}
                    onChange={(e) => setAdjForm({ ...adjForm, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    required
                  />
                </div>
                {adjForm.adjustment_type === 'deduction' && (
                  <div className="md:col-span-1">
                    <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      Expense Category
                      {!adjForm.category_manual && adjForm.reason && guessExpenseCategory(adjForm.reason) && (
                        <span className="ml-2 text-[10px] font-normal text-emerald-700 normal-case tracking-normal">auto-detected</span>
                      )}
                    </label>
                    <select
                      data-testid="adj-expense-category"
                      value={adjForm.expense_category}
                      onChange={(e) => setAdjForm({ ...adjForm, expense_category: e.target.value, category_manual: true })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      {EXPENSE_CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Reason</label>
                  <input
                    type="text"
                    data-testid="adj-reason-input"
                    value={adjForm.reason}
                    onChange={(e) => {
                      const newReason = e.target.value;
                      setAdjForm((prev) => {
                        const next = { ...prev, reason: newReason };
                        // Auto-suggest the expense category only when the admin hasn't manually picked one yet
                        if (prev.adjustment_type === 'deduction' && !prev.category_manual) {
                          const guessed = guessExpenseCategory(newReason);
                          if (guessed) next.expense_category = guessed;
                        }
                        return next;
                      });
                    }}
                    placeholder={adjForm.adjustment_type === 'compensation' ? 'e.g. On-time delivery bonus' : 'e.g. Cargo damage claim'}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Applies To</label>
                  <div className="flex items-center gap-4 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <input type="radio" name="applies_to" value="all" checked={adjForm.applies_to === 'all'} onChange={(e) => setAdjForm({ ...adjForm, applies_to: e.target.value })} data-testid="adj-applies-all" />
                      <span className="text-sm text-slate-700">All drivers ({entries.length})</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <input type="radio" name="applies_to" value="selected" checked={adjForm.applies_to === 'selected'} onChange={(e) => setAdjForm({ ...adjForm, applies_to: e.target.value })} data-testid="adj-applies-selected" />
                      <span className="text-sm text-slate-700">Specific drivers</span>
                    </label>
                  </div>
                  {adjForm.applies_to === 'selected' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-slate-200 rounded-md p-3 bg-white">
                      {entries.map((e) => (
                        <label key={e.driver_id} className="flex items-center gap-2 text-sm cursor-pointer" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          <input
                            type="checkbox"
                            data-testid={`adj-driver-${e.driver_id}`}
                            checked={adjForm.selected_drivers.includes(e.driver_id)}
                            onChange={() => toggleDriverSelect(e.driver_id)}
                          />
                          <span className="text-slate-700">{e.driver_name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  data-testid="submit-adjustment-button"
                  disabled={savingAdj}
                  className="bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all disabled:opacity-50"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  {savingAdj ? 'Saving...' : 'Save Adjustment'}
                </button>
                <button
                  type="button"
                  data-testid="cancel-adjustment-button"
                  onClick={() => { setShowAdjForm(false); setAdjError(''); }}
                  className="text-slate-700 px-4 py-2 rounded-md font-semibold hover:bg-slate-100 transition-all"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {adjustments.length === 0 ? (
            <p data-testid="no-adjustments-message" className="text-sm text-slate-500 italic" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No custom adjustments yet.</p>
          ) : (
            <div className="space-y-2">
              {adjustments.map((adj) => (
                <div key={adj.id} data-testid={`adjustment-row-${adj.id}`} className="flex items-center justify-between p-3 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    {adj.adjustment_type === 'compensation' ? <PlusCircle className="w-5 h-5 text-green-600" /> : <MinusCircle className="w-5 h-5 text-red-600" />}
                    <div>
                      <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {adj.adjustment_type === 'compensation' ? '+' : '−'} ${adj.amount.toFixed(2)}
                        {adj.expense_category && (
                          <span className="ml-2 inline-block px-2 py-0.5 bg-orange-100 text-orange-800 text-[10px] font-bold uppercase tracking-widest rounded">
                            {EXPENSE_CATEGORY_LABEL[adj.expense_category] || adj.expense_category}
                          </span>
                        )}
                        <span className="text-slate-700 font-normal"> • {adj.reason}</span>
                      </p>
                      <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {adj.applies_to_driver_ids && adj.applies_to_driver_ids.length > 0
                          ? `Applies to ${adj.applies_to_driver_ids.length} driver(s)`
                          : 'Applies to all drivers'}
                      </p>
                    </div>
                  </div>
                  <button data-testid={`delete-adjustment-${adj.id}`} onClick={() => handleDeleteAdj(adj.id)} className="text-red-600 hover:text-red-800">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6">
          <h2 className="text-xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Work Sans, sans-serif' }}>Driver Breakdown ({entries.length})</h2>

          {entries.length === 0 ? (
            <p className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No driver entries in this payroll.</p>
          ) : (
            <div className="space-y-3">
              {entries.map((e) => {
                const isOpen = expanded[e.id];
                return (
                  <div key={e.id} data-testid={`entry-${e.id}`} className="border border-slate-200 rounded-md">
                    <div className="w-full p-3 sm:p-4 flex flex-wrap items-center justify-between gap-2 hover:bg-slate-50 transition-colors">
                      <button
                        data-testid={`entry-toggle-${e.id}`}
                        onClick={() => setExpanded((prev) => ({ ...prev, [e.id]: !prev[e.id] }))}
                        className="flex-1 min-w-0 flex items-center justify-between text-left gap-2"
                      >
                      <div className="text-left min-w-0">
                        <p className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          <span className="truncate">{e.driver_name || e.driver_email}</span>
                          <EmailStatusPill entry={e} />
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          {e.entry_kind === 'staff'
                            ? <span data-testid={`entry-salary-badge-${e.id}`}>Salary · {`$${Number(e.pay_rate).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} / week • Office staff{e.staff_role ? ` (${e.staff_role.replace('_', ' ')})` : ''}</span>
                            : <>{e.pay_type ? `${PAY_TYPE_LABELS[e.pay_type]} @ ${e.pay_type === 'percentage' ? `${e.pay_rate}%` : `$${e.pay_rate}`}` : 'No pay structure'}
                          {' • '}{e.load_count} load{e.load_count === 1 ? '' : 's'}</>}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 sm:gap-6 shrink-0">
                        <div className="text-right hidden sm:block">
                          <p className="text-xs text-slate-500 uppercase tracking-widest" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Gross</p>
                          <p data-testid={`entry-gross-${e.id}`} className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>${Number(e.gross_pay || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-500 uppercase tracking-widest" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Net</p>
                          <p data-testid={`entry-net-${e.id}`} className={`text-lg font-bold ${Number(e.net_pay || 0) < 0 ? 'text-red-600' : 'text-green-700'}`} style={{ fontFamily: 'Work Sans, sans-serif' }}>${Number(e.net_pay || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                        </div>
                        {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                      </div>
                      </button>
                      {isAdmin && !e.is_staff && (
                        <button
                          data-testid={`add-load-${e.id}`}
                          onClick={(ev) => { ev.stopPropagation(); setAddLoadEntry(e); }}
                          title="Add a missing load or authorized adjustment to this driver's payroll"
                          className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-50 border border-emerald-600 rounded-md px-3 py-2 transition-colors"
                          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                        >
                          <Plus className="w-4 h-4" />
                          <span className="hidden sm:inline">Add Load</span>
                        </button>
                      )}
                      <button
                        data-testid={`download-paystub-${e.id}`}
                        onClick={(ev) => { ev.stopPropagation(); handleDownloadPaystub(e); }}
                        title="Download paystub PDF"
                        className="flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] hover:bg-blue-50 border border-[#002FA7] rounded-md px-3 py-2 transition-colors"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                      >
                        <Download className="w-4 h-4" />
                        <span className="hidden sm:inline">Paystub</span>
                      </button>
                      <button
                        data-testid={`resend-paystub-${e.id}`}
                        onClick={(ev) => { ev.stopPropagation(); handleResendPaystub(e); }}
                        disabled={resendingEntry === e.id}
                        title="Email this paystub to the driver"
                        className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 border border-slate-300 rounded-md px-3 py-2 transition-colors disabled:opacity-50"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                      >
                        {resendingEntry === e.id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Mail className="w-4 h-4" />}
                        <span className="hidden sm:inline">{resendingEntry === e.id ? 'Sending…' : 'Resend email'}</span>
                      </button>
                    </div>

                    {isOpen && (
                      <div className="border-t border-slate-200 p-4 bg-slate-50 space-y-4">
                        {e.entry_kind !== 'staff' && (
                          <PeriodDeductionsPanel driverId={e.driver_id} driverName={e.driver_name || e.driver_email}
                            weekStart={payroll.week_start} weekEnd={payroll.week_end} locked={payroll.locked}
                            applied={periodDeductions.applied} pending={periodDeductions.pending}
                            onChanged={() => { periodDeductions.refresh(); }} />
                        )}
                        {e.breakdown && (
                          <div className="p-3 bg-white border border-slate-200 rounded-md">
                            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Gross Calculation</p>
                            <p className="text-sm text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{e.breakdown.basis || e.breakdown.note || '—'}</p>
                          </div>
                        )}

                        <EntryAdminFeeRow payroll={payroll} entry={e} isAdmin={isAdmin} onApplied={(d) => { if (d?.id && d.id !== payrollId) navigate(`/payroll/${d.id}`, { replace: true }); else fetchDetail(); }} />

                        {e.standing_deductions && e.standing_deductions.filter((sd) => sd.deduction_type !== 'admin_fee').length > 0 && (
                          <div className="p-3 bg-white border border-slate-200 rounded-md">
                            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Standing Deductions ({e.standing_deductions.filter((sd) => sd.deduction_type !== 'admin_fee').length})</p>
                            <div className="space-y-1.5">
                              {e.standing_deductions.filter((sd) => sd.deduction_type !== 'admin_fee').map((sd, idx) => (
                                <div key={idx} className="flex items-center justify-between text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                                  <span className="text-slate-700">{DEDUCTION_LABELS[sd.deduction_type] || sd.deduction_type} <span className="text-xs text-slate-500">({sd.txn_date ? [new Date(`${sd.txn_date}T00:00:00`).toLocaleDateString(), sd.description, sd.truck_unit && `Unit ${sd.truck_unit}`, sd.gallons && `${sd.gallons} gal`, sd.receipt_ref && `Receipt ${sd.receipt_ref}`].filter(Boolean).join(' · ') : sd.basis})</span>
                                    {sd.expense_id && <button type="button" data-testid={`payroll-view-receipt-${idx}`} onClick={() => openReceipt(sd.expense_id)} className="ml-2 text-xs text-[#002FA7] hover:underline">view receipt</button>}
                                  </span>
                                  <span className="text-orange-700 font-medium">− ${sd.amount.toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {e.adjustments && e.adjustments.length > 0 && (
                          <div className="p-3 bg-white border border-slate-200 rounded-md">
                            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Custom Adjustments ({e.adjustments.length})</p>
                            <div className="space-y-1.5">
                              {e.adjustments.map((adj) => (
                                <div key={adj.id} className="flex items-center justify-between text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                                  <span className="text-slate-700">
                                    {adj.expense_category && (
                                      <span className="inline-block mr-2 px-1.5 py-0.5 bg-orange-100 text-orange-800 text-[10px] font-bold uppercase tracking-widest rounded">
                                        {EXPENSE_CATEGORY_LABEL[adj.expense_category] || adj.expense_category}
                                      </span>
                                    )}
                                    {adj.reason}
                                  </span>
                                  <span className={adj.adjustment_type === 'compensation' ? 'text-blue-700 font-medium' : 'text-red-700 font-medium'}>
                                    {adj.adjustment_type === 'compensation' ? '+' : '−'} ${adj.amount.toFixed(2)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {e.loads && e.loads.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Delivered Loads ({e.loads.length})</p>
                            <div className="space-y-2">
                              {e.loads.map((ld) => (
                                <div key={ld.load_id} className={`bg-white border rounded-md p-3 text-sm ${ld.manual ? 'border-emerald-300' : 'border-slate-200'}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid={`entry-load-${ld.load_id}`}>
                                  <p className="text-slate-900 font-medium">{ld.load_ref ? `${ld.load_ref} · ` : ''}{ld.pickup_location}{ld.delivery_location ? ` → ${ld.delivery_location}` : ''}</p>
                                  {ld.manual && (
                                    <p className="text-[11px] text-emerald-800 mt-0.5" data-testid={`entry-load-manual-${ld.load_id}`}>
                                      <span className="font-bold uppercase">Manual · {ld.manual.type === 'link_existing' ? 'linked existing load' : 'payroll-only load'}{ld.manual.period_exception ? ' · period exception' : ''}</span>
                                      {' '}by {ld.manual.added_by_name} {ld.manual.added_at ? `on ${new Date(ld.manual.added_at).toLocaleString()}` : ''} — {ld.manual.reason}
                                    </p>
                                  )}
                                  <p className="text-xs text-slate-500 mt-0.5">
                                    {ld.cargo_description && <span>{ld.cargo_description} • </span>}
                                    {ld.distance && <span>{ld.distance} • </span>}
                                    Delivered {ld.delivered_at ? new Date(ld.delivered_at).toLocaleDateString() : '-'}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {addLoadEntry && (
        <AddLoadToPayrollModal
          payroll={payroll}
          entry={addLoadEntry}
          onClose={() => setAddLoadEntry(null)}
          onDone={(newPayrollId) => { if (newPayrollId && newPayrollId !== payrollId) navigate(`/payroll/${newPayrollId}`); else fetchDetail(); }}
        />
      )}
    </DashboardLayout>
  );
};

export default PayrollDetail;
